export type DiagnosticsAccessLevel = 'none' | 'developer' | 'temporary';

const DEVELOPER_DIAGNOSTICS_EMAILS = ['umarinzalda@gmail.com'];
const PENDING_DIAGNOSTICS_EMAIL_KEY = 'FP_PENDING_DIAGNOSTICS_EMAIL';
const ACTIVE_DIAGNOSTICS_EMAIL_KEY = 'FP_ACTIVE_DIAGNOSTICS_EMAIL';

const getLocalStorage = (): Storage | null => {
  if (typeof window === 'undefined') return null;
  return window.localStorage;
};

export const normalizeDiagnosticsEmail = (email?: string | null): string =>
  email?.trim().toLowerCase() ?? '';

export const isDeveloperDiagnosticsEmail = (email?: string | null): boolean => {
  const normalizedEmail = normalizeDiagnosticsEmail(email);
  return Boolean(
    normalizedEmail && DEVELOPER_DIAGNOSTICS_EMAILS.includes(normalizedEmail),
  );
};

export const getPendingDiagnosticsEmail = (): string | null => {
  return getLocalStorage()?.getItem(PENDING_DIAGNOSTICS_EMAIL_KEY) ?? null;
};

export const setPendingDiagnosticsEmail = (email: string): string => {
  const normalizedEmail = normalizeDiagnosticsEmail(email);
  if (!normalizedEmail) return '';

  getLocalStorage()?.setItem(PENDING_DIAGNOSTICS_EMAIL_KEY, normalizedEmail);
  return normalizedEmail;
};

export const clearPendingDiagnosticsEmail = (): void => {
  getLocalStorage()?.removeItem(PENDING_DIAGNOSTICS_EMAIL_KEY);
};

export const clearActiveDiagnosticsAccess = (): void => {
  getLocalStorage()?.removeItem(ACTIVE_DIAGNOSTICS_EMAIL_KEY);
};

export const activatePendingDiagnosticsAccessForLogin = (email: string): void => {
  const storage = getLocalStorage();
  if (!storage) return;

  const loginEmail = normalizeDiagnosticsEmail(email);
  const pendingEmail = normalizeDiagnosticsEmail(
    storage.getItem(PENDING_DIAGNOSTICS_EMAIL_KEY),
  );

  if (!pendingEmail) {
    clearActiveDiagnosticsAccess();
    return;
  }

  storage.removeItem(PENDING_DIAGNOSTICS_EMAIL_KEY);

  if (loginEmail === pendingEmail) {
    storage.setItem(ACTIVE_DIAGNOSTICS_EMAIL_KEY, loginEmail);
    return;
  }

  clearActiveDiagnosticsAccess();
};

export const getDiagnosticsAccessLevel = (
  email?: string | null,
): DiagnosticsAccessLevel => {
  const normalizedEmail = normalizeDiagnosticsEmail(email);
  if (isDeveloperDiagnosticsEmail(normalizedEmail)) return 'developer';

  const activeDiagnosticsEmail = normalizeDiagnosticsEmail(
    getLocalStorage()?.getItem(ACTIVE_DIAGNOSTICS_EMAIL_KEY),
  );

  if (activeDiagnosticsEmail) return 'temporary';

  const pendingDiagnosticsEmail = normalizeDiagnosticsEmail(getPendingDiagnosticsEmail());
  if (normalizedEmail && pendingDiagnosticsEmail === normalizedEmail) {
    activatePendingDiagnosticsAccessForLogin(normalizedEmail);
    return 'temporary';
  }

  return 'none';
};
